"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { getMessaging, getToken, onMessage, Messaging } from "firebase/messaging";
import { saveFcmToken } from "@/app/api/service/firebase";
import NotificationDialog from "@/app/components/popup/notification";
import NotificationBlockedDialog from "@/app/components/popup/notification-blocked";
import {
    getUserNotificationPreference,
    setUserNotificationPreference,
} from "@/app/utils/notificationpreference";

// Firebase config
const firebaseConfig = {
    apiKey: "AIzaSyBEXFmUvsxJBq1Rz5__Dk4FE1PMP4bL5ps",
    authDomain: "incredmoney.firebaseapp.com",
    projectId: "incredmoney",
    storageBucket: "incredmoney.appspot.com",
    messagingSenderId: "150530677387",
    appId: "1:150530677387:web:57f2f870135fbd4300128e",
};

// Utility: Validate VAPID key (URL-safe Base64)
function validateVapidKey(key: string): boolean {
    if (!key) {
        console.error("❌ VAPID key is missing or empty");
        return false;
    }

    try {
        // Convert URL-safe base64 to standard base64
        let paddedKey = key.replace(/-/g, "+").replace(/_/g, "/");
        const pad = paddedKey.length % 4;
        if (pad !== 0) {
            paddedKey += "=".repeat(4 - pad);
        }

        atob(paddedKey); // Will throw if invalid
        return true;
    } catch (e) {
        console.error("❌ Invalid VAPID key format", e);
        return false;
    }
}

let _messaging: Messaging | null = null;

function getFirebaseApp(): FirebaseApp {
    // Prevent duplicate initialization
    if (getApps().length > 0) {
        return getApps()[0];
    }
    return initializeApp(firebaseConfig);
}

function getFirebaseMessaging(): Messaging {
    if (!_messaging) {
        _messaging = getMessaging(getFirebaseApp());
    }
    return _messaging;
}

// Token storage utility
const TOKEN_KEY = "fcm_token";

function storeToken(token: string) {
    localStorage.setItem(TOKEN_KEY, token);
}

function getStoredToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
}

// Context Setup
interface FcmTokenContextType {
    token: string | null;
    refreshToken: () => Promise<void>;
}

const FcmTokenContext = createContext<FcmTokenContextType>({
    token: null,
    refreshToken: async () => {},
});

// 🔄 Token API registration
async function triggerTokenRegistration(token: string) {
    try {
        await saveFcmToken(token);
    } catch (error) {
        console.error("Failed to register FCM token:", error);
    }
}

function isIOS(): boolean {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
        return false;
    }

    // Check using userAgent only, avoiding deprecated platform property
    return /iPad|iPhone|iPod|Mac|Macintosh|Darwin/i.test(navigator.userAgent);
}

export const FcmTokenProvider = ({ children }: { children: ReactNode }) => {
    const [token, setToken] = useState<string | null>(null);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [showNotificationBlocked, setShowNotificationBlocked] = useState(false);

    const forceResetServiceWorkerAndPush = async () => {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((r) => r.unregister()));

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
            await subscription.unsubscribe();
        }

        console.log("Force reset of service worker and push subscription complete");
    };

    const refreshToken = async () => {
        try {
            if (typeof window === "undefined") return;

            // Check if path contains /advisory and skip FCM setup if it does
            if (window.location.pathname.includes("/advisory")) {
                console.log("Advisory path detected, skipping FCM token registration");
                return;
            }

            if (Notification.permission !== "granted") {
                console.warn(
                    "Skipping token refresh because notification permission is not granted",
                );
                return; // 🚫 Don't proceed if permission isn't granted
            }

            const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;

            if (!vapidKey || !validateVapidKey(vapidKey)) {
                console.error("🚫 Invalid or missing VAPID key");
                setToken(null);
                return;
            }

            const messaging = getFirebaseMessaging();

            const currentToken = await getToken(messaging, {
                vapidKey,
            });

            if (!currentToken) {
                console.log("No registration token available. Request permission to generate one.");
                setToken(null);
                return;
            }

            const storedToken = getStoredToken();
            const isNewToken = !storedToken || storedToken !== currentToken;

            if (isNewToken) {
                console.log("FCM token has changed or is new. Registering new token.");
                await triggerTokenRegistration(currentToken);
                storeToken(currentToken);
            }

            setToken(currentToken);
        } catch (error: unknown) {
            console.error("Error retrieving FCM token:", error);

            // Define Firebase error type
            interface FirebaseError {
                code?: string;
                name?: string;
            }

            // Handle token-not-registered or push errors by resetting
            if (
                typeof error === "object" &&
                error !== null &&
                (("code" in error &&
                    (error as FirebaseError).code ===
                        "messaging/registration-token-not-registered") ||
                    ("name" in error && (error as FirebaseError).name === "AbortError"))
            ) {
                console.warn(
                    "Token is invalid or PushService failed. Attempting to force reset...",
                );

                try {
                    await forceResetServiceWorkerAndPush();

                    // Retry after reset
                    const messaging = getFirebaseMessaging();
                    const newToken = await getToken(messaging, {
                        vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
                    });

                    if (newToken) {
                        await triggerTokenRegistration(newToken);
                        storeToken(newToken);
                        setToken(newToken);
                        console.log("Recovered new token after reset.");
                    } else {
                        console.log("Still failed to get token after reset.");
                        setToken(null);
                    }
                } catch (resetError) {
                    console.error("Failed to recover after reset:", resetError);
                    setToken(null);
                }
            } else {
                setToken(null);
            }
        }
    };

    useEffect(() => {
        if (typeof window === "undefined") return;

        // First, check if Notification API exists at all
        const notificationSupported = "Notification" in window;

        // 🔍 Check if the app is running inside a WebView
        const isWebView = () => {
            const userAgent = navigator.userAgent || navigator.vendor;
            return /wv|WebView|(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent);
        };

        if (isWebView() || window?.ReactNativeWebView) {
            console.warn("WebView detected — skipping FCM logic.");
            return;
        }

        if (isIOS()) {
            console.warn("(iOS) Skipping automatic FCM request. Showing custom prompt instead.");
            return;
        }

        const userPref = getUserNotificationPreference();

        // Only proceed if Notification API is supported
        if (notificationSupported) {
            if (Notification.permission === "granted") {
                refreshToken();

                // ✅ Safe to initialize messaging and listen to foreground messages
                const messaging = getFirebaseMessaging();
                const unsubscribe = onMessage(messaging, (payload) => {
                    console.log("Message received in foreground:", payload);
                });

                return () => {
                    unsubscribe();
                };
            }

            if (userPref !== "denied") {
                setShowDialog(true);
            }
        } else {
            console.warn("Notification API not supported in this environment");
            // You could implement an alternative notification system here
        }
    }, []);

    const handleAccept = async () => {
        if (!("Notification" in window)) {
            console.warn("Notification API not supported");
            setShowDialog(false);
            return;
        }

        const permission = await Notification.requestPermission();

        if (permission === "granted") {
            setShowDialog(false);
            setUserNotificationPreference("granted");
            await refreshToken();
        } else if (permission === "denied") {
            setShowDialog(false);
            setUserNotificationPreference("denied");
            setShowNotificationBlocked(true);
        }
    };

    const handleDecline = () => {
        setUserNotificationPreference("denied");
        setShowDialog(false);
    };

    return (
        <FcmTokenContext.Provider value={{ token, refreshToken }}>
            {children}
            <NotificationDialog
                open={showDialog}
                onAccept={handleAccept}
                onDecline={handleDecline}
            />
            <NotificationBlockedDialog
                open={showNotificationBlocked}
                onAccept={() => setShowNotificationBlocked(false)}
            />
        </FcmTokenContext.Provider>
    );
};

export const useFcmToken = () => useContext(FcmTokenContext);